/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { resolvePublicImageUrl } from "../../helper/helper";
import { fileToBase64, splitBase64 } from "../../utils/fileUtils";
import { postDeleteGalleryImages } from "../../utils/galleryApiMap";
import { Empty } from "../tournamentPage/MatchesPanel";
import { IcSearch } from "../tournamentPage/icons";
import type { TeamCtx } from "./TeamPage";

export type Photo = { id: string; raw: string; src: string };
const MAX_MB = 10;
const MAX_FILES = 20;

/** Team photos plus the viewer's permissions, straight from the API (it decides who may add or delete). */
export const usePhotos = (ts?: string) => {
  const q = useQuery({
    queryKey: ["tgPhotos", ts],
    enabled: !!ts,
    staleTime: 30_000,
    queryFn: async () => {
      const data: any = (await Api.get("getAllGalleryForPlayer/", { params: { teamSnapShotId: ts } })).data;
      const list: any[] = Array.isArray(data) ? data : (data?.imageList ?? data?.data?.imageList ?? []);
      const items: Photo[] = list
        .map((x, i) => {
          const raw = String(typeof x === "string" ? x : (x?.imageUrl ?? x?.url ?? x?.imageUri ?? "")).trim();
          return { id: String(x?.id ?? raw ?? i), raw, src: resolvePublicImageUrl(raw) ?? "" };
        })
        .filter((p) => p.raw && p.src);
      return { items, canAdd: !!data?.canAddImages, canDelete: !!data?.canDeleteImages };
    },
  });
  return { items: q.data?.items ?? [], canAdd: q.data?.canAdd ?? false, canDelete: q.data?.canDelete ?? false, isLoading: q.isLoading, isError: q.isError, refetch: q.refetch };
};

const sizeLabel = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const UploadModal = ({ ts, teamName, onClose }: { ts: string; teamName: string; onClose: () => void }) => {
  const qc = useQueryClient();
  const [files, setFiles] = useState<{ file: File; url: string; err?: string }[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const good = files.filter((f) => !f.err);

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  useEffect(() => () => files.forEach((f) => URL.revokeObjectURL(f.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    Array.from(list).forEach((file) => {
      if (next.length >= MAX_FILES) return;
      const err = !file.type.startsWith("image/") ? "Not an image" : file.size > MAX_MB * 1024 * 1024 ? `${sizeLabel(file.size)} · too large, max ${MAX_MB} MB` : undefined;
      next.push({ file, url: URL.createObjectURL(file), err });
    });
    setFiles(next);
  };

  const upload = useMutation({
    mutationFn: async () => {
      const images = await Promise.all(good.map(async ({ file }) => ({ base64Image: splitBase64(await fileToBase64(file)).data, fileType: "PNG" })));
      await Api.post(QUERY_KEY.multiplecreateGallery, { albumDate: null, albumName: null, imageType: "GALLERY_IMAGE", images, leagueId: null, teamSnapShotId: ts, tournamentId: null });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["tgPhotos", ts] });
      toast.success(`${good.length} ${good.length === 1 ? "photo" : "photos"} added`);
      onClose();
    },
    onError: () => toast.error("Failed to upload photos."),
  });

  return (
    <>
      <div className="mscrim" onClick={onClose} />
      <div className="modal2" role="dialog" aria-modal="true" aria-label="Add photos">
        <div className="mh"><div><h3>Add photos</h3><span>to {teamName} · visible to everyone who can see this team</span></div><button className="dx" onClick={onClose} aria-label="Close">×</button></div>
        <div className="mb">
          <div className="drop" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files); }}>
            <b>Drag photos here</b><span>JPG, PNG or HEIC · up to {MAX_MB} MB each · {MAX_FILES} at a time</span>
            <button className="btn" onClick={() => input.current?.click()}>Choose files</button>
            <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
          </div>
          {files.length > 0 && (
            <ul className="flist">
              {files.map((f, i) => (
                <li key={f.url} className={f.err ? "bad" : ""}>
                  <img src={f.url} alt="" /><span><b>{f.file.name}</b><small>{f.err ?? `${sizeLabel(f.file.size)} · ✓ Ready`}</small></span>
                  <button className="link" onClick={() => { URL.revokeObjectURL(f.url); setFiles(files.filter((_, j) => j !== i)); }}>Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mf">
          <span>{good.length ? `${good.length} ${good.length === 1 ? "photo" : "photos"} ready${files.length - good.length ? ` · ${files.length - good.length} can't be added` : ""}` : "Choose the photos to add."}</span>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!good.length || upload.isPending} onClick={() => upload.mutate()}>{upload.isPending ? "Adding…" : good.length ? `Add ${good.length} ${good.length === 1 ? "photo" : "photos"}` : "Add photos"}</button>
        </div>
      </div>
    </>
  );
};

const Lightbox = ({ ctx, photos, start, canDelete, onClose }: { ctx: TeamCtx; photos: Photo[]; start: number; canDelete: boolean; onClose: () => void }) => {
  const qc = useQueryClient();
  const [i, setI] = useState(start);
  const [confirm, setConfirm] = useState(false);
  const p = photos[i];
  const step = useCallback((d: number) => { setConfirm(false); setI((x) => Math.min(photos.length - 1, Math.max(0, x + d))); }, [photos.length]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") step(-1); if (e.key === "ArrowRight") step(1); };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose, step]);
  useEffect(() => { if (!p) onClose(); }, [p, onClose]);

  const del = useMutation({
    mutationFn: () => postDeleteGalleryImages([p.raw]),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["tgPhotos", ctx.ts] });
      toast.success("Photo deleted");
      setConfirm(false);
      setI((x) => Math.max(0, Math.min(x, photos.length - 2)));
    },
    onError: () => toast.error("Failed to delete the photo."),
  });
  if (!p) return null;
  const share = async () => { try { await navigator.clipboard.writeText(p.src); toast.success("Photo link copied"); } catch { /* ignore */ } };
  return (
    <div className="lbx" role="dialog" aria-modal="true" aria-label="Photo viewer">
      <div className="lbh"><b>{ctx.name}</b><span>Photo {i + 1} of {photos.length}</span><button className="dx" onClick={onClose} aria-label="Close">×</button></div>
      <div className="lbs">
        <button className="lbn" onClick={() => step(-1)} disabled={i === 0} aria-label="Previous photo">‹</button>
        <img src={p.src} alt={`${ctx.name} photo ${i + 1}`} />
        <button className="lbn" onClick={() => step(1)} disabled={i === photos.length - 1} aria-label="Next photo">›</button>
      </div>
      <div className="lbf">
        {confirm ? (
          <span className="lbc">Delete this photo? It's removed for everyone.<button className="btn danger" disabled={del.isPending} onClick={() => del.mutate()}>{del.isPending ? "Deleting…" : "Delete"}</button><button className="btn" onClick={() => setConfirm(false)}>Cancel</button></span>
        ) : (
          <>
            <button className="btn" onClick={() => void share()}>Share</button>
            {canDelete && <button className="btn danger" onClick={() => setConfirm(true)}>Delete</button>}
          </>
        )}
      </div>
    </div>
  );
};

const PhotosTab = ({ ctx }: { ctx: TeamCtx }) => {
  const ph = usePhotos(ctx.ts);
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  if (ph.isLoading) return <div className="pgrid2" aria-hidden="true">{Array.from({ length: 10 }, (_, i) => <i key={i} className="sk phsk" />)}</div>;
  if (ph.isError) return <Empty icon={<IcSearch />} title="Couldn't load photos" text="Check your connection and try again." acts={<button className="btn primary" onClick={() => void ph.refetch()}>Try again</button>} />;

  return (
    <>
      <div className="tool tg-tool">
        <div className="summary" style={{ marginLeft: 0 }}>{ph.items.length} {ph.items.length === 1 ? "photo" : "photos"}</div>
        <span style={{ flex: 1 }} />
        {ph.canAdd ? <button className="btn primary" onClick={() => setAdding(true)}>+ Add photos</button> : <span className="viewonly">View only · photos are added by the team admin or organiser</span>}
      </div>
      {ph.items.length === 0 ? (
        <Empty
          icon={<IcSearch />}
          title="No photos yet"
          text={ph.canAdd ? "Add photos from matches, practice and team events." : `Photos of ${ctx.name} will appear here when the team admin or the organiser adds them.`}
          acts={ph.canAdd ? <button className="btn primary" onClick={() => setAdding(true)}>Add photos</button> : undefined}
        />
      ) : (
        <div className="pgrid2">
          {ph.items.map((p, i) => (
            <button key={p.id} className="ph" onClick={() => setOpen(i)} aria-label={`Open photo ${i + 1}`}><img src={p.src} alt="" loading="lazy" /></button>
          ))}
        </div>
      )}
      {open != null && <Lightbox ctx={ctx} photos={ph.items} start={open} canDelete={ph.canDelete} onClose={() => setOpen(null)} />}
      {adding && <UploadModal ts={ctx.ts} teamName={ctx.name} onClose={() => setAdding(false)} />}
    </>
  );
};

export default PhotosTab;
